import React, { useEffect, useMemo, useRef } from 'react';
import { CANONICAL_SPECTRUM } from '../utils/chartUtils';
const TYPE_ORDER = ['Advertising', 'Form', 'Rate', 'Compliance/Other'];
const TYPE_COLORS = {
  Advertising: CANONICAL_SPECTRUM[0],
  Form: CANONICAL_SPECTRUM[10],
  Rate: CANONICAL_SPECTRUM[20],
  'Compliance/Other': CANONICAL_SPECTRUM[29]
};
const CO_COLORS = [
  CANONICAL_SPECTRUM[2],
  CANONICAL_SPECTRUM[7],
  CANONICAL_SPECTRUM[12],
  CANONICAL_SPECTRUM[17],
  CANONICAL_SPECTRUM[22],
  CANONICAL_SPECTRUM[27]
];
function shortWeekHeader(wk) {
  const m = String(wk).match(/W(\d{1,2})$/i);
  return m ? `Wk ${m[1]}` : String(wk).slice(-4);
}
function prodSectionMax(categories, byCompany, companyKeys) {
  let max = 1;
  categories.forEach(cat => {
    companyKeys.forEach(co => {
      const v = byCompany[co]?.[cat] || 0;
      if (v > max) max = v;
    });
  });
  return max;
}
function ProductBars({
  categories,
  byCompany,
  companyKeys
}) {
  const maxVal = prodSectionMax(categories, byCompany, companyKeys);
  return <>
    {categories.map(cat => <div key={cat} className="prod-row">
      <span className="prod-lbl">{cat}</span>
      <div className="prod-bars">
        {companyKeys.map((co, idx) => {
          const val = byCompany[co]?.[cat] || 0;
          if (!val) return null;
          const color = CO_COLORS[idx % CO_COLORS.length];
          return <div key={`${cat}-${co}`} className="mini-bar">
            <div className="mini-track">
              <div className="mini-fill" style={{
                width: `${Math.round(val / maxVal * 100)}%`,
                background: color
              }} />
            </div>
            <span className="mini-num" style={{
              color
            }}>
              {val}
            </span>
          </div>;
        })}
      </div>
    </div>)}
  </>;
}
function WeeklyHeatmap({
  weekLabelsSorted,
  weeklyCadenceByLabel,
  companyOrder,
  displayLabels,
  subjectSeriesKey
}) {
  const maxV = useMemo(() => {
    let m = 1;
    companyOrder.forEach(co => {
      const row = weeklyCadenceByLabel[co] || {};
      weekLabelsSorted.forEach(wk => {
        const v = row[wk] || 0;
        if (v > m) m = v;
      });
    });
    return m;
  }, [weekLabelsSorted, weeklyCadenceByLabel, companyOrder]);
  return <>
    <div className="hm-header competitiveintelligencereport-style-1">
      {weekLabelsSorted.map(wk => <span key={wk}>{shortWeekHeader(wk)}</span>)}
    </div>
    <div>
      {companyOrder.map((co, idx) => {
        const vals = weekLabelsSorted.map(wk => weeklyCadenceByLabel[co]?.[wk] || 0);
        const isTa = co === subjectSeriesKey;
        const rr = 27;
        const gg = 58;
        const bb = 107;
        return <div key={co} className={`hm-row${isTa ? ' ta' : ''}`}>
          <span className="hm-lbl" style={isTa ? {
            fontWeight: 600,
            color: 'var(--c-black)',
            fontSize: '10.5px'
          } : {
            color: 'var(--text3)'
          }}>
            {displayLabels[idx] || co}
          </span>
          <div className="hm-cells">
            {vals.map((v, i) => <div key={i} className="hm-cell" style={v === 0 ? {
              background: 'var(--c-white)',
              color: 'var(--text3)'
            } : (() => {
              const intensity = 0.18 + v / maxV * 0.82;
              return {
                background: `rgba(${rr},${gg},${bb},${intensity.toFixed(2)})`,
                color: intensity > 0.5 ? '#fff' : 'var(--text2)'
              };
            })()}>
              {v === 0 ? '—' : v}
            </div>)}
          </div>
        </div>;
      })}
    </div>
  </>;
}

/**
 * NLG-style layout + Chart.js + API KPIs/charts; narratives/actions show Coming soon until AI populates report.narratives.
 */
export default function CompetitiveIntelligenceReport({
  report
}) {
  const canvasRef = useRef(null);
  const kpis = report.kpis || {};
  const subjectSeriesKey = report.subject?.seriesKey || '';
  const subjectLabel = report.subject?.label || 'Subject';
  const subjectShort = useMemo(() => {
    const base = report.subject?.client_name || subjectLabel;
    const s = base.split('(')[0].trim();
    return s.length > 28 ? `${s.slice(0, 26)}…` : s;
  }, [report, subjectLabel]);
  const companyOrder = useMemo(() => report?.charts?.companyOrder ?? [], [report]);
  const displayLabels = useMemo(() => {
    const dl = report?.charts?.companyDisplayLabels;
    return dl?.length ? dl : companyOrder;
  }, [report, companyOrder]);
  const vol = useMemo(() => report?.charts?.volumeByCompanyAndType ?? {}, [report]);
  const weekLabelsSorted = useMemo(() => report?.charts?.weekLabelsSorted ?? [], [report]);
  const weeklyCadenceByLabel = useMemo(() => report?.charts?.weeklyCadenceByLabel ?? {}, [report]);
  const topStates = useMemo(() => report?.charts?.topStates ?? [], [report]);
  const pd = useMemo(() => report?.charts?.productDepth || {
    annuities: {
      categories: [],
      byCompany: {}
    },
    life: {
      categories: [],
      byCompany: {}
    }
  }, [report]);
  const maxStateTotal = useMemo(() => {
    let m = 1;
    topStates.forEach(d => {
      if (d.totalFilings > m) m = d.totalFilings;
    });
    return m;
  }, [topStates]);
  useEffect(() => {
    const Chart = window.Chart;
    const canvas = canvasRef.current;
    if (!Chart || !canvas || !companyOrder.length) return;
    const existing = Chart.getChart(canvas);
    if (existing) existing.destroy();
    const labels = displayLabels.length ? displayLabels : companyOrder;
    const datasets = TYPE_ORDER.map(t => ({
      label: t,
      data: companyOrder.map(k => vol[k]?.[t] ?? 0),
      backgroundColor: TYPE_COLORS[t],
      categoryPercentage: 0.72,
      barPercentage: 0.92,
      maxBarThickness: 220,
      borderRadius: 2
    }));
    const textColor = 'rgba(0,0,0,0.40)';
    const gridColor = 'rgba(0,0,0,0.06)';
    const baseScale = {
      ticks: {
        color: textColor,
        font: {
          size: 9,
          family: 'ABC Diatype, system-ui, sans-serif'
        }
      },
      grid: {
        color: gridColor
      },
      border: {
        display: false
      }
    };
    const chartInstance = new Chart(canvas, {
      type: 'bar',
      data: {
        labels,
        datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            titleFont: {
              size: 9
            },
            bodyFont: {
              size: 9
            }
          }
        },
        scales: {
          x: {
            ...baseScale,
            stacked: true,
            ticks: {
              ...baseScale.ticks,
              maxRotation: 25
            }
          },
          y: {
            ...baseScale,
            stacked: true,
            beginAtZero: true
          }
        }
      }
    });
    return () => {
      chartInstance.destroy();
    };
  }, [report, companyOrder, displayLabels, vol]);
  const alertState = kpis.alertState;
  const competitorsLine = (report.competitors && report.competitors.length ? report.competitors.join(' · ') : 'Selected competitors') + ' \u00a0|\u00a0 Internal Confidential';
  return <div className="report-outer report-outer--embedded">
    <div className="report-scope">
      <div className="report-page">
        <div className="header">
          <div className="header-left">
            <div className="header-divider" />
            <div className="header-text">
              <span className="report-label">Insurance Regulatory Intelligence</span>
              <span className="report-title">
                {subjectLabel} — Competitive Filing Intelligence
              </span>
            </div>
          </div>
          <div className="header-right">
            <span className="week-badge">{report.period?.label || 'Period'}</span>
            <br />
            vs. {competitorsLine}
          </div>
        </div>

        <div className="kpi-grid">
          <div className="kpi good">
            <div className="kpi-lbl">{subjectShort} total filings</div>
            <div className="kpi-val">{kpis.subjectTotalFilings ?? '—'}</div>
            <div className="kpi-sub cg">
              {kpis.competitorAvgTotalFilings != null ? `Near competitor avg of ${kpis.competitorAvgTotalFilings}` : 'Competitor average —'}
            </div>
          </div>
          <div className="kpi warn">
            <div className="kpi-lbl">Advertising concentration</div>
            <div className="kpi-val">{kpis.advertisingConcentrationPct ?? '—'}%</div>
            <div className="kpi-sub cr">
              {kpis.advertisingFilings != null && kpis.subjectTotalFilings != null ? `${kpis.advertisingFilings} of ${kpis.subjectTotalFilings} are advertising filings` : 'Share of advertising filings'}
            </div>
          </div>
          <div className="kpi good">
            <div className="kpi-lbl">IUL/EIA product focus</div>
            <div className="kpi-val">{kpis.productFocusFilingsIndexedOrIulHeuristic ?? '—'}</div>
            <div className="kpi-sub cg">Indexed / annuity-related heuristic from TOI fields</div>
          </div>
          <div className="kpi warn">
            <div className="kpi-lbl">State presence</div>
            <div className="kpi-val">{kpis.subjectStatePresence ?? '—'}</div>
            <div className="kpi-sub cr">
              {kpis.avgCompetitorStatePresence != null ? `vs. competitor avg ${kpis.avgCompetitorStatePresence} states` : 'Distinct states with filings'}
            </div>
          </div>
          <div className="kpi warn">
            <div className="kpi-lbl">Top-10 busy states with subject = 0</div>
            <div className="kpi-val">{kpis.topTenBusyStatesWhereSubjectAbsent ?? '—'}</div>
            <div className="kpi-sub cr">{kpis.absentInBusyStatesPreview || '—'}</div>
          </div>
        </div>

        <div className="report-grid--asymmetric">
          <div className="report-card">
            <div className="report-card-title">Filing volume by company &amp; type</div>
            <div className="legend">
              <span className="li">
                <span className="sq competitiveintelligencereport-style-2" />
                Advertising
              </span>
              <span className="li">
                <span className="sq competitiveintelligencereport-style-3" />
                Form
              </span>
              <span className="li">
                <span className="sq competitiveintelligencereport-style-4" />
                Rate
              </span>
              <span className="li">
                <span className="sq competitiveintelligencereport-style-5" />
                Compliance/Other
              </span>
            </div>
            <div className="competitiveintelligencereport-style-6">
              <canvas ref={canvasRef} id="volChart" />
            </div>
          </div>
          <div className="report-card">
            <div className="report-card-title">Weekly filing cadence</div>
            {weekLabelsSorted.length === 0 ? <p className="report-note">No weekly buckets in this date range (needs fetched_at on snapshots).</p> : <>
              <WeeklyHeatmap weekLabelsSorted={weekLabelsSorted} weeklyCadenceByLabel={weeklyCadenceByLabel} companyOrder={companyOrder} displayLabels={displayLabels} subjectSeriesKey={subjectSeriesKey} />
              <p className="report-note">
                {report.narratives?.weeklyCadenceNote || 'Coming soon — narrative summary of weekly cadence (cells = filings per ISO week).'}
              </p>
            </>}
          </div>
        </div>

        <div className="report-grid--asymmetric">
          <div className="report-card">
            <div className="report-card-title">Product category depth — annuities &amp; life</div>
            <div className="legend competitiveintelligencereport-style-7">
              {companyOrder.map((co, idx) => <span key={co} className="li">
                <span className="sq" style={{
                  background: CO_COLORS[idx % CO_COLORS.length]
                }} />
                {displayLabels[idx] || co}
              </span>)}
            </div>
            <div className="prod-section-lbl">Annuities</div>
            <div id="prodAnn">
              <ProductBars categories={pd.annuities?.categories || []} byCompany={pd.annuities?.byCompany || {}} companyKeys={companyOrder} />
            </div>
            <div className="prod-section-lbl">Life Insurance</div>
            <div id="prodLife">
              <ProductBars categories={pd.life?.categories || []} byCompany={pd.life?.byCompany || {}} companyKeys={companyOrder} />
            </div>
          </div>

          <div className="report-card">
            <div className="report-card-title">Top states by total filing activity</div>
            <div id="statebars" className="competitiveintelligencereport-style-8">
              {topStates.map(d => {
                const alert = d.subjectFilings === 0 && d.totalFilings > 0;
                const totalW = Math.round(d.totalFilings / maxStateTotal * 100);
                const subjectBarW = d.subjectFilings > 0 ? Math.round(d.subjectFilings / maxStateTotal * 100) : 0;
                return <div key={d.state} className="state-row">
                  <span className="state-lbl" style={alert ? {
                    color: 'var(--c-orange)',
                    fontWeight: 600
                  } : {}}>
                    {d.state}
                  </span>
                  <div className="state-track">
                    <div style={{
                      width: `${totalW}%`,
                      background: alert ? 'var(--orange-bg)' : 'var(--c-blue-4)'
                    }} className="competitiveintelligencereport-style-9" />
                    {d.subjectFilings > 0 ? <div style={{
                      width: `${subjectBarW}%`
                    }} className="competitiveintelligencereport-style-10">
                      {subjectShort}:{d.subjectFilings}
                    </div> : null}
                  </div>
                  <span className="state-tot" style={{
                    color: alert ? 'var(--c-orange)' : 'var(--text3)',
                    fontWeight: alert ? 700 : 400
                  }}>
                    {d.totalFilings} total
                  </span>
                </div>;
              })}
            </div>
            <hr className="report-divider" />
            {alertState ? <>
              <div className="competitiveintelligencereport-style-11">
                {alertState.state} alert — {alertState.totalFilings} total filings; {subjectShort} = 0
              </div>
              <div className="competitiveintelligencereport-style-12">
                High-activity state with no subject filings in the selected window — review competitor mix in raw
                filings.
              </div>
            </> : null}
            <hr className="report-divider" />
            <div className="competitiveintelligencereport-style-13">
              Competitors filing where {subjectShort} is absent
            </div>
            {(report.competitorsFilingWhereSubjectAbsent || []).map(row => <div key={row.competitor} className="competitiveintelligencereport-style-14">
              <span className="competitiveintelligencereport-style-15">
                {row.competitor} ({row.statesWhereSubjectAbsent?.length || 0} states):
              </span>{' '}
              {(row.statesWhereSubjectAbsent || []).map(st => <span key={st} className="report-chip--gap">
                {st}
              </span>)}
            </div>)}
          </div>
        </div>

        <div className="report-grid--half">
          <div className="report-card competitiveintelligencereport-style-16">
            <div className="report-card-title">Key signals</div>
            <p className="report-note competitiveintelligencereport-style-17">
              {report.narratives?.keySignals || 'Coming soon — AI-generated highlights from the metrics and charts above.'}
            </p>
          </div>
        </div>

        <div className="report-card">
          <div className="report-card-title competitiveintelligencereport-style-18">
            Recommended actions
          </div>
          <p className="report-note competitiveintelligencereport-style-19">
            {report.narratives?.recommendedActions || `Coming soon — prioritized recommendations tailored to ${subjectLabel}.`}
          </p>
        </div>

        <div className="footer">
          <div className="competitiveintelligencereport-style-20">
            <span>
              Powered by <strong>www.thecomply.ai</strong>
            </span>
          </div>
          <span>
            Data source: State DOI public filing databases (SERFF) · Period: {report.period?.label || '—'} · Derived
            from serff_filing_snapshots
          </span>
          <span>
            CONFIDENTIAL — Internal Use Only · {subjectLabel}
          </span>
        </div>
      </div>
    </div>
  </div>;
}